import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Plane, Home, Phone, Clock, Loader2, AlertTriangle, Navigation2, RefreshCw } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

/**
 * Public live tracking page (`/track/:bookingId?token=xxx`).
 * Shows trip details + driver pin on a map (OpenStreetMap, no API key).
 * Auto-refreshes every 25 seconds.
 */
export default function TrackBookingPage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/bookings/${bookingId}/track`, { params: { token } });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to load tracking data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!bookingId || !token) {
      setError("Missing booking ID or tracking token.");
      setLoading(false);
      return;
    }
    load();
    const id = setInterval(load, 25_000);
    return () => clearInterval(id);
  }, [bookingId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-rose-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center" data-testid="track-error">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-1">Cannot Load Tracking</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const lat = data?.driver_latitude;
  const lng = data?.driver_longitude;
  const hasDriverLoc = typeof lat === "number" && typeof lng === "number";
  const mapSrc = hasDriverLoc
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.05}%2C${lat - 0.03}%2C${lng + 0.05}%2C${lat + 0.03}&layer=mapnik&marker=${lat}%2C${lng}`
    : null;
  const lastUpd = data?.driver_location_updated_at
    ? new Date(data.driver_location_updated_at)
    : null;
  const minsAgo = lastUpd ? Math.round((Date.now() - lastUpd.getTime()) / 60000) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-3 md:px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <Card className="border-0 shadow-md" data-testid="track-summary-card">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                  <Navigation2 className="w-6 h-6 text-blue-600" />
                  Live Trip Tracking
                </h1>
                <p className="text-xs text-muted-foreground">Confirmation #: <span className="font-bold">{data.confirmation_number}</span></p>
              </div>
              <Button size="icon" variant="outline" onClick={load} disabled={refreshing} data-testid="track-refresh-btn">
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
            </div>
            <div className="space-y-1.5 text-sm mt-3">
              <p><span className="text-muted-foreground">Passenger:</span> <span className="font-semibold">{data.passenger_name}</span></p>
              <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /> {data.travel_date} at {data.travel_time}</p>
              <p className="flex items-center gap-2"><Plane className="w-4 h-4 text-blue-600" /> Pickup: {data.pickup_location}</p>
              <p className="flex items-center gap-2"><Home className="w-4 h-4 text-emerald-600" /> Drop-off: {data.dropoff_location || "—"}</p>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge className="bg-blue-100 text-blue-700">{data.booking_status}</Badge>
              {data.passenger_status && <Badge className="bg-purple-100 text-purple-700">{data.passenger_status}</Badge>}
            </div>
          </CardContent>
        </Card>

        {/* Driver Card */}
        {data.assigned_driver_name ? (
          <Card className="border-0 shadow-md" data-testid="track-driver-card">
            <CardContent className="p-4 md:p-5">
              <p className="text-xs text-muted-foreground mb-1">Your Driver</p>
              <h3 className="text-lg font-bold mb-2">{data.assigned_driver_name}</h3>
              {data.assigned_driver_phone && (
                <a href={`tel:${data.assigned_driver_phone}`} data-testid="track-call-driver-btn">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full">
                    <Phone className="w-4 h-4 mr-1" /> Call Driver
                  </Button>
                </a>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-md bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-sm text-amber-900">
              ⏳ Driver will be assigned closer to your trip date.
            </CardContent>
          </Card>
        )}

        {/* Map */}
        <Card className="border-0 shadow-md overflow-hidden" data-testid="track-map-card">
          <CardContent className="p-0">
            {mapSrc ? (
              <>
                <iframe src={mapSrc} title="driver-track-map" width="100%" height="320" className="border-0" data-testid="track-iframe-map" />
                <div className="bg-blue-50 px-3 py-2 text-xs text-blue-900 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" />
                  Live driver location {minsAgo != null ? `(updated ${minsAgo}m ago)` : ""}
                </div>
              </>
            ) : (
              <div className="h-60 flex flex-col items-center justify-center gap-2 text-muted-foreground bg-gray-50">
                <MapPin className="w-8 h-8" />
                <p className="text-sm text-center px-4">
                  {data.assigned_driver_name
                    ? "Driver hasn't shared their location yet — they will once tracking is enabled."
                    : "A driver will be assigned soon. Tracking activates when they're en route."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground text-center">
          Page auto-refreshes every 25 seconds. Map: © OpenStreetMap contributors.
        </p>
      </div>
    </div>
  );
}
